import { type ComponentType, lazy } from "react";

/**
 * Drop-in replacement for `React.lazy` that survives the classic
 * post-deploy chunk-load failure:
 *
 *   "Failed to fetch dynamically imported module: /assets/foo-OLD.js"
 *
 * This happens whenever a user has an old `index.html` open (cached in
 * memory or by the browser HTTP cache) and we ship a new build. The
 * stale bundle references chunk hashes like `employees-OLD.js`, but
 * the new deploy's `dist/` only contains `employees-NEW.js`, so the
 * dynamic import 404s.
 *
 * The fix: detect the chunk-load failure, force a hard reload once
 * (which fetches the fresh `index.html` + new hashes), and short-circuit
 * the lazy resolver. We gate on a sessionStorage flag so a genuinely
 * broken chunk doesn't trigger an infinite reload loop.
 */
const RELOAD_FLAG = "hwb-chunk-reload-attempted";

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  // Vite's actual message; covers the Chrome/Firefox/Safari variants.
  return /Failed to (?:fetch dynamically imported module|import)|Loading (?:chunk|CSS chunk)/i.test(
    err.message,
  );
}

export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Clear the flag once a chunk loads successfully again.
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* SSR / strict-mode storage failures are not interesting here */
      }
      return mod;
    } catch (err) {
      if (isChunkLoadError(err)) {
        let alreadyTried = false;
        try {
          alreadyTried = sessionStorage.getItem(RELOAD_FLAG) === "1";
          sessionStorage.setItem(RELOAD_FLAG, "1");
        } catch {
          /* storage unavailable — fall through and let the boundary show */
        }
        if (!alreadyTried) {
          window.location.reload();
          // Hang forever; the reload will replace this document anyway.
          return await new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });
}
